import type { IconName } from '@customerio/pluma-icons';
import type { SearchResult } from '@/services/auth/types';
import { resolveAssetUrl } from '@/utils/resolve-asset-url';
import {
  ACTION_TYPE_ICONS,
  CAMPAIGN_TYPE_ICONS,
  CUSTOM_FORM_TYPE_IMAGE_FILENAMES,
  CUSTOM_FORM_TYPE_PLUMA_ICONS,
  TEMPLATE_TYPE_ICONS,
} from '../constants';
import { getIntegrationIconUrl, type IntegrationCatalog } from './integration-catalog';

// Covers all navigation entries currently indexed by Vertex (Discovery Engine).
// Will be replaced when navigation results move to FE-generated local data.
// Ordered most-specific first so the first match wins.
const NAVIGATION_ICON_PATTERNS: [string, IconName][] = [
  // Broadcasts (specific sub-routes before generic)
  ['broadcasts/api-triggered', 'api-triggered-broadcast'],
  ['broadcasts/newsletter', 'newsletter'],
  ['broadcasts/one_time_sends', 'newsletter'],
  ['/broadcasts', 'broadcast'],
  // Segments
  ['segments/explore', 'segment-data-driven'],
  ['/segments', 'segment'],
  // Messaging
  ['/campaigns', 'campaigns'],
  ['/transactional', 'transactional-api'],
  ['/anonymous', 'anonymous'],
  ['/outbox', 'delivery-log'],
  ['/message_activity', 'delivery-log'], // Deliveries & Drafts → Message activity rename: same icon, flag-on URL segment
  // Audience
  ['/people', 'person'],
  ['/profiles', 'person'], // People → Profiles rename: flag-on URL segment, same icon
  ['/objects', 'object'],
  ['/goals', 'goal'],
  // Review
  ['/activity_logs', 'activity'],
  ['/profile_activity', 'activity'], // Activity Logs → Profile activity rename: same icon, flag-gated URL segment
  ['/analysis', 'analysis'],
  ['/reports', 'analysis'], // Analysis → Reports rename: same icon, flag-gated URL segment
  ['/health', 'analysis'],
  // Content
  ['message-library', 'folder'],
  ['/assets', 'image'],
  ['/layouts', 'layout'],
  ['/snippets', 'liquid'],
  // Data
  ['data-index', 'data-index'],
  ['integrations/reporting-webhooks', 'webhook'],
  ['/integrations', 'integrations'],
  ['/imports', 'import'],
  ['/exports', 'export'],
  ['/collections', 'database'],
  // Design Studio
  ['design-studio', 'design-studio-new'],
  // Agent / AI
  ['/agent', 'sparkle'],
  // Pipelines
  ['/pipelines', 'data-index'],
  // Settings – actions (channel-specific before generic)
  ['actions/email', 'email'],
  ['actions/push', 'mobile'],
  ['actions/in_app', 'in-app'],
  ['actions/slack', 'slack'],
  ['actions/sms', 'sms'],
  ['actions/twilio', 'sms'],
  ['actions/whatsapp', 'brand/whatsapp'],
  ['actions/messaging_limit', 'message-limit'],
  ['actions/time_zone_match', 'time-window'],
  ['actions/url_params', 'link'],
  // Settings – workspace-specific
  ['settings/ai-business-profile', 'sparkle'],
  ['settings/llm_configuration', 'sparkle'],
  ['settings/api_credentials', 'api-credentials'],
  ['settings/api-keys', 'api-credentials'],
  ['settings/geofences', 'geofences'],
  ['settings/webhook_configuration', 'webhook'],
  ['settings/subscription_center', 'email'],
  ['settings/merge_options', 'merge'],
  ['settings/suppressions', 'blocks'],
  ['settings/audit-logs', 'audit'],
  // Settings – account-level
  ['/settings/security', 'lock-secure'],
  ['/settings/team', 'manage-team'],
  ['/settings/roles', 'manage-team'],
  ['/settings/billing', 'billing-history'],
  ['/settings/privacy', 'data-privacy'],
  ['/settings/personal', 'person'],
  ['/settings/workspaces', 'workspaces'],
  ['/settings/experimental-features', 'sparkle'],
  ['/settings/account', 'workspace-settings'],
  // Home / Dashboard (after more-specific paths that also contain "home" or "dashboard")
  ['/home', 'home'],
  ['/dashboard', 'home'],
  // Generic settings fallback
  ['/settings', 'workspace-settings'],
];

function getNavigationIcon(url: string): IconName {
  const path = URL.canParse(url) ? new URL(url).pathname : url;
  for (const [pattern, icon] of NAVIGATION_ICON_PATTERNS) {
    if (path.includes(pattern)) return icon;
  }
  return 'arrow-right';
}

// Kinds whose icon doesn't depend on the result's data fields.
const STATIC_KIND_ICONS: Partial<Record<SearchResult['kind'], IconName>> = {
  newsletter: 'newsletter',
  goal: 'goal',
  transactional_message: 'transactional-api',
  anonymous_in_app: 'in-app',
  snippet: 'liquid',
  email_layout: 'layout',
  collection: 'database',
  customer: 'person',
  docs: 'connect-file',
  source: 'data-index',
  destination: 'integrations',
  ad_audience: 'targeting',
  reporting_webhook: 'webhook',
  cdp_out: 'data-index',
  data_out: 'export',
};

export function getIconNameFromSearchResult(result: SearchResult): IconName {
  const staticIcon = STATIC_KIND_ICONS[result.kind];
  if (staticIcon != null) return staticIcon;

  switch (result.kind) {
    case 'campaign':
      return CAMPAIGN_TYPE_ICONS[result.other.campaign_type] ?? 'campaigns';
    case 'template':
      return TEMPLATE_TYPE_ICONS[result.other.template_type] ?? 'layout';
    case 'segment':
      return result.other.segment_type === 'static' ? 'segment-manual' : 'segment-data-driven';
    case 'action':
      return ACTION_TYPE_ICONS[result.other.action_type] ?? 'help';
    case 'navigation':
      return getNavigationIcon(result.url);
    case 'custom_form':
      return CUSTOM_FORM_TYPE_PLUMA_ICONS[result.other.form_type] ?? 'form';
    case 'object_type':
      return (result.other.icon != null && result.other.icon !== '' ? result.other.icon : 'object') as IconName;
    default:
      return 'search';
  }
}

export function getFormTypeImageUrl(formType: string): string | undefined {
  const filename = CUSTOM_FORM_TYPE_IMAGE_FILENAMES[formType];
  return filename != null ? resolveAssetUrl(`hydra-assets/icons/${filename}`) : undefined;
}

export function getResultImageUrl(result: SearchResult, catalog: IntegrationCatalog): string | undefined {
  if (result.kind === 'source' || result.kind === 'destination') {
    return getIntegrationIconUrl(result.other.type, catalog);
  }
  if (result.kind === 'custom_form') {
    return getFormTypeImageUrl(result.other.form_type);
  }
  return undefined;
}
